import './Segmented.scss';

export interface SegmentedOption<V> {
  readonly value: V;
  readonly label: string;
  /** Semantic colour applied when this option is the selected one (e.g. `danger` for Fail, `success` for Success). */
  readonly intent?: 'success' | 'danger';
}

interface SegmentedProps<V> {
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly options: readonly SegmentedOption<V>[];
  readonly disabled?: boolean;
  /** Accessible label for the group (e.g. "Override node instance status"). */
  readonly ariaLabel?: string;
}

/**
 * A segmented control: a pill-shaped row of mutually-exclusive options with the selected one highlighted (e.g. Fail |
 * Success, Continue | Abort). A styled single-choice picker for a small, fixed option set. Generic — owns its styling.
 */
export const Segmented = <V,>({ value, onChange, options, disabled = false, ariaLabel }: SegmentedProps<V>) => (
  <div className="segmented" role="group" aria-label={ariaLabel}>
    {options.map((option) => {
      const active = option.value === value;
      const intentClass = active && option.intent ? ` segmented__option--${option.intent}` : '';
      return (
        <button
          key={String(option.value)}
          type="button"
          className={`segmented__option${active ? ' segmented__option--active' : ''}${intentClass}`}
          aria-pressed={active}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
